/** 销售订单列表/详情展示字段投影（从 order-detail 全量 payload 选取） */

import {
  SALES_ORDER_STATUS_LABELS,
  SALES_ORDER_STOCK_MODE_LABELS,
} from '@/tiktok-platform/constants/tiktokApiCatalog';
import {
  displayValue,
  formatMoney,
  mapLabel,
  normalizeImageUrl,
} from '@/tiktok-platform/modules/aftersales/returnOrderDisplay';
import { pickField } from '@/tiktok-platform/utils/tiktokResponse';

import i18n from '@/i18n';
import { fixedUi } from '@/i18n/fixedUi';

const ORDER_TEXT = {
  "预计总收入": "income",
  "商品总金额": "productTotal",
  "供货价合计": "costTotal",
  "买家运费": "buyerShip",
  "店铺优惠": "storeDiscount",
  "活动优惠": "promoDiscount",
  "活动提报优惠": "reportDiscount",
  "履约服务费": "performanceFee",
  "佣金": "commission",
  "消费税": "saleTax",
  "欧洲VAT": "euVat",
  "墨西哥税费": "mxTax",
  "销售价格": "salePrice",
  "下单": "ordered",
  "付款": "paid",
  "签收": "signed",
  "站点": "site",
  "订单数量": "orderQty",
  "订单号": "orderNo",
  "订单状态": "orderStatus",
  "订单类型": "orderType",
  "销售站点": "salesSite",
  "库存模式": "stockMode",
  "打单状态": "printStatus",
  "发货方式": "shipMethod",
  "下单方式": "placeMethod",
  "可选物流": "optionalLogistics",
  "原订单号": "originOrderNo",
  "是否COD": "isCod",
  "超限拆包": "overLimit",
  "销售范围": "salesArea",
  "店铺 code": "storeCode",
  "订单币种": "orderCurrency",
  "销售币种": "saleCurrency",
  "买家支付运费": "buyerPaidShip",
  "店铺优惠合计": "storeDiscountSum",
  "活动优惠合计": "promoDiscountSum",
  "活动提报优惠合计": "reportDiscountSum",
  "总佣金": "commissionSum",
  "总履约服务费": "performanceFeeSum",
  "总服务费": "serviceFeeSum",
  "总消费税": "saleTaxSum",
  "总欧洲VAT": "euVatSum",
  "总墨西哥税费": "mxTaxSum",
  "国家": "country",
  "省/州": "province",
  "城市": "city",
  "邮编": "postCode",
  "下单时间": "orderTime",
  "付款时间": "payTime",
  "下发时间": "allocateTime",
  "签收时间": "receiptTime",
  "信息更新时间": "msgUpdateTime",
  "打印面单时间": "printingTime",
  "预约发货时间": "scheduleDeliveryTime",
  "卖家交货时间": "sellerDeliveryTime",
  "物流取件时间": "pickUpTime",
  "要求打单时间": "requestPrintTime",
  "要求发货时间": "needDeliveryTime",
  "要求揽收时间": "requestHandoverTime",
  "要求签收时间": "requestSignTime",
  "拒收时间": "rejectionTime",
  "退款时间": "returnTime",
  "报损时间": "lossTime",
  "仓库交货时间": "warehouseDeliveryTime",
  "期望揽收时间": "expectedCollectTime",
  "正常订单": "typeNormal",
  "换货订单": "typeExchange",
  "认证仓转自发货": "typeCertifiedSelf",
  "认证仓订单": "typeCertified",
  "可处理": "printOk",
  "不可处理": "printNo",
  "未选发货方式": "logisticsUnset",
  "平台合作物流": "logisticsPlatform",
  "商家自发货": "logisticsSeller",
  "平台指定物流": "placePlatform",
  "在线下单": "placeOnline",
  "是": "yes",
  "否": "no",
  "TikTok自营库存": "stockTiktok",
  "TEMU自营库存": "stockTemu",
  "SHEIN自营库存": "stockShein",
  "备货到TikTok仓(SFS)": "stockSfsTiktok",
  "备货到TEMU仓(SFS)": "stockSfsTemu",
  "备货到SHEIN仓(SFS)": "stockSfsShein",
  "商家仓发货": "stockSeller",
  "全部": "tabAll",
  "待付款": "tabUnpaid",
  "保留中": "tabOnHold",
  "待发货": "tabAwaitShip",
  "部分发货": "tabPartialShip",
  "待揽收": "tabAwaitCollect",
  "运输中": "tabInTransit",
  "已送达": "tabDelivered",
  "已完成": "tabCompleted",
  "已取消": "tabCancelled",
  "待处理": "tabPending",
  "待TEMU发货": "tabTemuShip",
  "待SHEIN发货": "tabSheinShip",
  "已发货": "tabShipped",
  "已签收": "tabSigned",
  "用户已退款": "tabUserRefund",
  "已报损": "tabDamaged",
  "已拒收": "tabRejected",
  "已退款": "tabRefunded",
  "平台优惠": "platformDisc",
  "买家实付": "buyerPaid",
  "履约方式": "fulfillment",
  "配送选项": "deliveryOption",
  "配送类型": "deliveryType",
  "物流商": "carrier",
  "运单号": "trackingNo",
  "仓库": "warehouse",
  "货到付款": "cod",
  "样品单": "sampleOrder",
  "拆合包": "splitCombine",
  "合包": "combined",
  "拆包": "split",
  "支付方式": "payMethod",
  "买家留言": "buyerMsg",
  "卖家备注": "sellerNote",
  "取消原因": "cancelReason",
  "取消发起方": "cancelBy",
  "卖家": "roleSeller",
  "买家": "roleBuyer",
  "系统": "roleSystem",
  "发货时间": "shipTime",
  "状态更新时间": "statusUpdateTime",
  "最晚发货时间": "rtsSla",
  "最晚揽收时间": "ttsSla",
  "自动取消时间": "autoCancelTime",
  "要求送达时间": "reqDeliveryTime",
  "揽收时间": "collectTime",
  "送达时间": "deliveredTime",
  "支付币种": "payCurrency",
  "商品原价": "origProductPrice",
  "商品小计": "productSubtotal",
  "卖家折扣": "sellerDisc",
  "平台折扣": "platformDiscAmt",
  "支付渠道折扣": "payChannelDisc",
  "运费原价": "origShipping",
  "运费卖家折扣": "shipSellerDisc",
  "运费平台折扣": "shipPlatformDisc",
  "税费": "tax",
  "商品税": "productTax",
  "运费税": "shippingTax",
  "零售配送费": "retailDeliveryFee",
  "手续费": "handlingFee",
  "收件人": "recipient",
  "电话": "phone",
  "详细地址": "addressDetail",
  "投放位置": "dropOff",
  "区/县": "district",
  "卖家自发货": "ffSeller",
  "TikTok 履约": "ffTiktok",
  "Tokopedia 履约": "ffTokopedia",
  "送货上门": "homeDelivery",
  "自提点": "collectionPoint",
  "现货": "otNormal",
  "预售": "otPreOrder",
  "按单生产": "otMadeToOrder",
  "缺货预订": "otBackOrder",
  "虚拟商品": "otVirtual",
  "直播抽奖": "otLottery",
  "拍卖": "otAuction",
};

function tr(raw) {
  if (raw == null || raw === '') return raw;
  const key = ORDER_TEXT[raw];
  if (key) {
    const translated = i18n.t(`orderList.${key}`, { ns: 'workbench', defaultValue: '' });
    if (translated) return translated;
  }
  return fixedUi(raw);
}

function trKnown(raw) {
  if (raw == null || raw === '') return raw;
  return ORDER_TEXT[raw] ? tr(raw) : raw;
}


export { displayValue, formatMoney, normalizeImageUrl };

export const ORDER_TYPE_LABELS = {
  1: '正常订单',
  2: '换货订单',
  4: '认证仓转自发货',
  5: '认证仓订单',
};

export const PRINT_ORDER_STATUS_LABELS = {
  1: '可处理',
  2: '不可处理',
};

export const ORDER_LOGISTICS_TYPE_LABELS = {
  0: '未选发货方式',
  1: '平台合作物流',
  2: '商家自发货',
};

export const ORDER_PLACE_TYPE_LABELS = {
  1: '平台指定物流',
  2: '在线下单',
};

export function salesStatusLabel(code) {
  return tr(mapLabel(SALES_ORDER_STATUS_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function stockModeLabelOf(code) {
  return tr(mapLabel(SALES_ORDER_STOCK_MODE_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function legacyOrderTypeLabel(code) {
  return tr(mapLabel(ORDER_TYPE_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function printOrderStatusLabel(code) {
  return tr(mapLabel(PRINT_ORDER_STATUS_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function orderLogisticsTypeLabel(code) {
  return tr(mapLabel(ORDER_LOGISTICS_TYPE_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function orderPlaceTypeLabel(code) {
  return tr(mapLabel(ORDER_PLACE_TYPE_LABELS, code) || (code == null || code === '' ? '' : String(code)));
}

export function formatTiktokTime(raw) {
  if (raw == null || raw === '') return '';
  const numeric = typeof raw === 'number' || /^\d+$/.test(String(raw).trim());
  if (numeric) {
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) {
      const ms = n < 1e12 ? n * 1000 : n;
      const date = new Date(ms);
      if (!Number.isNaN(date.getTime())) {
        const pad = (value) => String(value).padStart(2, '0');
        return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`;
      }
    }
  }
  const s = String(raw).trim();
  if (!s) return '';
  return s.replace('T', ' ').replace(/\.\d{3}.*/, '').replace(/\+\d{4}$/, '').trim();
}

/** 列表行商品摘要 */
export function listOrderProductSummary(row) {
  const list = row?.orderGoodsInfoList || row?.goodsInfoList || row?.orderGoodsList;
  const currency = pickField(row, 'saleCurrency', 'orderCurrency', 'currency') || '';
  const incomeText = formatMoney(
    currency,
    pickField(row, 'estimatedGrossIncome', 'orderAmount', 'productTotalPrice'),
  );
  if (!Array.isArray(list) || list.length === 0) {
    const payloadItems = Array.isArray(row?.payload?.line_items) ? row.payload.line_items : [];
    if (payloadItems.length) {
      return listOrderProductSummary({ ...row, orderGoodsInfoList: payloadItems.map((li) => ({
        goodsTitle: li?.product_name || li?.sku_name || '',
        productId: li?.product_id || '',
        skuId: li?.sku_id || '',
        skuName: li?.sku_name || '',
        sellerSku: li?.seller_sku || '',
        imageUrl: li?.sku_image || '',
      })) });
    }
    return {
      title: '',
      imageUrl: '',
      productId: '',
      merchantSku: '',
      skuCode: '',
      skuName: '',
      goodsCount: 0,
      incomeText,
    };
  }
  const first = list[0] || {};
  const skuCode = pickField(first, 'skuId', 'skuCode', 'sku_id') || '';
  let merchantSku = pickField(first, 'sellerSku', 'supplierSku', 'merchantSku', 'seller_sku') || '';
  if (merchantSku && skuCode && String(merchantSku).toLowerCase() === String(skuCode).toLowerCase()) {
    merchantSku = '';
  }
  const payloadItems = Array.isArray(row?.payload?.line_items) ? row.payload.line_items : [];
  const payloadFirst = payloadItems[0] || {};
  return {
    title: pickField(first, 'goodsTitle', 'product_name', 'sku_name') || payloadFirst.product_name || payloadFirst.sku_name || '',
    imageUrl: normalizeImageUrl(pickField(first, 'imageUrl', 'sku_image', 'skuImage') || payloadFirst.sku_image),
    productId: pickField(first, 'productId', 'product_id', 'goodsId') || payloadFirst.product_id || '',
    merchantSku: merchantSku || payloadFirst.seller_sku || '',
    skuCode: skuCode || payloadFirst.sku_id || '',
    skuName: pickField(first, 'skuName', 'sku_name') || payloadFirst.sku_name || '',
    goodsCount: list.length || payloadItems.length,
    incomeText,
  };
}

/** 包裹/运单摘要（含同步入库的轨迹快照） */
export function listLogisticsSummary(row) {
  let packages = Array.isArray(row?.packageWaybillList) ? row.packageWaybillList : [];
  const lineItems = Array.isArray(row?.payload?.line_items) ? row.payload.line_items : [];
  if (!packages.length && Array.isArray(row?.payload?.packages)) {
    packages = row.payload.packages.map((pkg) => ({
      packageNo: pkg?.id || pkg?.package_id || '',
      waybillNo: pkg?.tracking_number || '',
      carrier: pkg?.shipping_provider_name || '',
    }));
  }
  packages = packages.map((pkg) => {
    const packageNo = String(pickField(pkg, 'packageNo') || '').trim();
    let waybillNo = String(pickField(pkg, 'waybillNo', 'deliveryNo') || '').trim();
    let carrier = String(pickField(pkg, 'carrier') || '').trim();
    const matched = lineItems.find((item) => !packageNo || String(item?.package_id || '') === packageNo) || lineItems[0];
    if (matched) {
      if (!waybillNo) waybillNo = String(matched.tracking_number || '').trim();
      if (!carrier) carrier = String(matched.shipping_provider_name || '').trim();
    }
    return { ...pkg, packageNo, waybillNo, carrier };
  });
  const tracks = Array.isArray(row?.ipeLogisticsTracks) ? row.ipeLogisticsTracks : [];
  const items = packages.map((pkg, index) => {
    const packageNo = String(pickField(pkg, 'packageNo') || '').trim();
    const waybillNo = String(pickField(pkg, 'waybillNo', 'deliveryNo') || '').trim();
    const carrier = String(pickField(pkg, 'carrier') || '').trim();
    const snap = tracks.find((t) => {
      const p = String(pickField(t, 'packageNo') || '').trim();
      const w = String(pickField(t, 'waybillNo') || '').trim();
      if (packageNo && p && p === packageNo) return true;
      if (waybillNo && w && w === waybillNo) return true;
      return false;
    }) || tracks[index] || null;
    const trackPayload = snap?.track || snap;
    const trackInfo = Array.isArray(trackPayload?.trackInfo)
      ? trackPayload.trackInfo
      : (Array.isArray(trackPayload) ? trackPayload : []);
    let latestNode = '';
    for (const info of trackInfo) {
      const tracking = Array.isArray(info?.tracking) ? info.tracking : [];
      if (!tracking.length) continue;
      const last = tracking[tracking.length - 1] || tracking[0];
      latestNode = pickField(last, 'nodeCodeName', 'description', 'nodeCode') || latestNode;
    }
    return {
      packageNo,
      waybillNo,
      carrier,
      latestNode,
      hasTrack: Boolean(snap && !snap.error && trackInfo.length),
      trackSnap: snap,
      error: snap?.error || '',
    };
  });
  return {
    items,
    count: items.length,
    hasAnyTrack: items.some((i) => i.hasTrack),
  };
}

/** 有有效价格才展示：空 / 缺失 / 数值为 0 均隐藏 */
function hasMeaningfulAmount(raw) {
  if (raw === undefined || raw === null || raw === '') return false;
  const text = String(raw).trim();
  if (!text || text === '-' || text === '—') return false;
  const n = Number(text.replace(/,/g, ''));
  if (Number.isFinite(n)) return n !== 0;
  return true;
}

/** 列表价格主字段（订单维度） */
export function listPriceRows(row) {
  const payment = row?.payload?.payment && typeof row.payload.payment === 'object' ? row.payload.payment : {};
  const currency = pickField(row, 'saleCurrency', 'orderCurrency', 'currency') || payment.currency || '';
  const money = (v) => formatMoney(currency, v);
  return [
    { label: tr('买家实付'), raw: pickField(row, 'estimatedGrossIncome', 'orderAmount', 'amount') || payment.total_amount },
    { label: tr('商品总金额'), raw: pickField(row, 'productTotalPrice') || payment.sub_total || payment.original_total_product_price },
    { label: tr('买家运费'), raw: pickField(row, 'sellerShippingFee') || payment.shipping_fee },
    { label: tr('店铺优惠'), raw: pickField(row, 'storeDiscountTotalPrice') || payment.seller_discount },
    { label: tr('平台优惠'), raw: pickField(row, 'promotionDiscountTotalPrice') || payment.platform_discount },
    { label: tr('履约服务费'), raw: pickField(row, 'totalPerformanceServiceCharge') },
    { label: tr('佣金'), raw: pickField(row, 'totalCommission') },
    { label: tr('消费税'), raw: pickField(row, 'totalSaleTax') },
    { label: tr('欧洲VAT'), raw: pickField(row, 'totalSellerRealTax') },
    { label: tr('墨西哥税费'), raw: pickField(row, 'totalMxTaxPrice') },
    { label: tr('销售价格'), raw: pickField(row, 'settleActuallyPrice') },
  ]
    .filter((r) => hasMeaningfulAmount(r.raw))
    .map((r) => ({ label: r.label, value: money(r.raw) }));
}

export function listPrimaryPrices(row) {
  const all = listPriceRows(row);
  return {
    primary: all.slice(0, 4),
    extra: all.slice(4),
  };
}

export function listDateRows(row) {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  return [
    { label: tr('下单'), value: formatTiktokTime(pickField(row, 'orderTime', 'createTime', 'create_time') || payload.create_time) },
    { label: tr('付款'), value: formatTiktokTime(pickField(row, 'paymentTime', 'paidTime', 'paid_time') || payload.paid_time) },
    { label: tr('签收'), value: formatTiktokTime(pickField(row, 'orderReceiptTime', 'deliveryTime', 'delivery_time') || payload.delivery_time) },
  ];
}

export function listOrderInfoRows(row) {
  const goodsList = row?.orderGoodsInfoList || row?.goodsInfoList || row?.orderGoodsList;
  const goodsCount = Array.isArray(goodsList) ? goodsList.length : Number(row?.goodsCount) || 0;
  return [
    { label: tr('站点'), value: pickField(row, 'salesSite', 'site') || row?.payload?.recipient_address?.region_code || '' },
    { label: tr('订单数量'), value: goodsCount > 0 ? `${goodsCount}` : '' },
  ].filter((r) => r.value);
}

const FULFILLMENT_LABELS = {
  FULFILLMENT_BY_SELLER: '卖家自发货',
  FULFILLMENT_BY_TIKTOK: 'TikTok 履约',
  FULFILLMENT_BY_DILAYANI_TOKOPEDIA: 'Tokopedia 履约',
};

const DELIVERY_TYPE_LABELS = {
  HOME_DELIVERY: '送货上门',
  COLLECTION_POINT: '自提点',
};

const TIKTOK_ORDER_TYPE_LABELS = {
  NORMAL: '现货',
  PRE_ORDER: '预售',
  MADE_TO_ORDER: '按单生产',
  BACK_ORDER: '缺货预订',
  VIRTUAL_ORDER: '虚拟商品',
  ZERO_LOTTERY: '直播抽奖',
  AUCTION: '拍卖',
};

function tiktokOrder(row) {
  if (!row || typeof row !== 'object') return {};
  const payload = row.payload && typeof row.payload === 'object' ? row.payload : {};
  return { ...payload, ...row };
}

function present(value) {
  return value != null && value !== '';
}

function yesNo(value) {
  if (value === true || value === 1 || value === '1' || value === 'true') return tr('是');
  if (value === false || value === 0 || value === '0' || value === 'false') return tr('否');
  return '';
}

function rowIf(label, value) {
  if (!present(value)) return null;
  const displayValue = typeof value === 'string' ? trKnown(value) : value;
  return { label: tr(label), value: displayValue };
}

function districtName(addr, level) {
  const list = Array.isArray(addr?.district_info) ? addr.district_info : [];
  const hit = list.find((item) => String(item?.address_level || '').toUpperCase() === level);
  return hit?.address_name || '';
}

export function detailBasicRows(row) {
  const order = tiktokOrder(row);
  const orderType = order.order_type || order.orderType;
  const fulfillment = order.fulfillment_type || order.fulfillmentType;
  const deliveryType = order.delivery_type || order.deliveryType;
  const splitTag = order.split_or_combine_tag;
  return [
    rowIf('订单号', pickField(order, 'orderNo', 'orderNumber', 'id')),
    rowIf('订单状态', salesStatusLabel(pickField(order, 'orderStatus', 'status'))),
    rowIf('订单类型', trKnown(TIKTOK_ORDER_TYPE_LABELS[orderType]) || (present(orderType) ? String(orderType) : '')),
    rowIf('履约方式', trKnown(FULFILLMENT_LABELS[fulfillment]) || fulfillment),
    rowIf('配送选项', order.delivery_option_name),
    rowIf('配送类型', trKnown(DELIVERY_TYPE_LABELS[deliveryType]) || deliveryType),
    rowIf('物流商', order.shipping_provider),
    rowIf('运单号', order.tracking_number),
    rowIf('仓库', order.warehouse_id),
    rowIf('货到付款', yesNo(order.is_cod)),
    rowIf('样品单', order.is_sample_order === true ? tr('是') : ''),
    rowIf('拆合包', splitTag === 'COMBINED' ? tr('合包') : (splitTag === 'SPLIT' ? tr('拆包') : '')),
    rowIf('支付方式', order.payment_method_name),
    rowIf('买家留言', order.buyer_message),
    rowIf('卖家备注', order.seller_note),
    rowIf('取消原因', order.cancel_reason),
    rowIf('取消发起方', order.cancellation_initiator === 'SELLER'
      ? tr('卖家')
      : (order.cancellation_initiator === 'BUYER'
        ? tr('买家')
        : (order.cancellation_initiator === 'SYSTEM' ? tr('系统') : order.cancellation_initiator))),
  ].filter(Boolean);
}

export function detailTimeRows(row) {
  const order = tiktokOrder(row);
  const keys = [
    ['下单时间', order.create_time || order.orderTime],
    ['付款时间', order.paid_time || order.paymentTime],
    ['发货时间', order.rts_time],
    ['状态更新时间', order.update_time || order.orderMsgUpdateTime],
    ['最晚发货时间', order.rts_sla_time],
    ['最晚揽收时间', order.tts_sla_time],
    ['自动取消时间', order.cancel_order_sla_time],
    ['要求送达时间', order.delivery_option_required_delivery_time],
    ['揽收时间', order.collection_time],
    ['送达时间', order.delivery_time || order.orderReceiptTime],
  ];
  return keys
    .map(([label, raw]) => rowIf(label, formatTiktokTime(raw)))
    .filter(Boolean);
}

export function detailPriceRows(row) {
  const order = tiktokOrder(row);
  const payment = order.payment && typeof order.payment === 'object' ? order.payment : {};
  const currency = payment.currency || pickField(order, 'saleCurrency', 'orderCurrency', 'currency') || '';
  const money = (key, fallback) => {
    const raw = payment[key] != null && payment[key] !== '' ? payment[key] : fallback;
    return hasMeaningfulAmount(raw) ? formatMoney(currency, raw) : '';
  };
  return [
    rowIf('支付币种', currency),
    rowIf('商品原价', money('original_total_product_price')),
    rowIf('商品小计', money('sub_total', pickField(order, 'productTotalPrice'))),
    rowIf('卖家折扣', money('seller_discount', pickField(order, 'storeDiscountTotalPrice'))),
    rowIf('平台折扣', money('platform_discount', pickField(order, 'promotionDiscountTotalPrice'))),
    rowIf('支付渠道折扣', money('payment_platform_discount')),
    rowIf('运费原价', money('original_shipping_fee')),
    rowIf('买家运费', money('shipping_fee', pickField(order, 'sellerShippingFee'))),
    rowIf('运费卖家折扣', money('shipping_fee_seller_discount')),
    rowIf('运费平台折扣', money('shipping_fee_platform_discount')),
    rowIf('税费', money('tax')),
    rowIf('商品税', money('product_tax')),
    rowIf('运费税', money('shipping_fee_tax')),
    rowIf('零售配送费', money('retail_delivery_fee')),
    rowIf('手续费', money('handling_fee')),
    rowIf('买家实付', money('total_amount', pickField(order, 'estimatedGrossIncome', 'orderAmount'))),
  ].filter(Boolean);
}

/**
 * 按订单状态和 shipping_type 决定可执行操作。
 * TIKTOK：平台物流，待发货走 Ship Package，待揽收打印面单。
 * SELLER：商家自发货，待发货上传运单。
 * FULFILLMENT_BY_TIKTOK / 虚拟发货不提供发货按钮。
 */
export function tiktokOrderActions(row) {
  const payload = row?.payload && typeof row.payload === 'object' ? row.payload : {};
  const order = { ...payload, ...(row && typeof row === 'object' ? row : {}) };
  const status = String(order.orderStatus || order.status || '').trim().toUpperCase();
  const shipping = String(order.shipping_type || '').trim().toUpperCase();
  const fulfillment = String(order.fulfillment_type || '').trim().toUpperCase();
  const none = { ship: false, uploadWaybill: false, printLabel: false, track: false };
  const sellerCanShip = !fulfillment || fulfillment === 'FULFILLMENT_BY_SELLER';
  if (!sellerCanShip || shipping === 'TIKTOK_DIGITAL') return none;
  const awaiting = status === 'AWAITING_SHIPMENT' || status === 'PARTIALLY_SHIPPING' || status === '1' || status === '2';
  const collecting = status === 'AWAITING_COLLECTION' || status === '3' || status === '7';
  const moving = status === 'IN_TRANSIT' || status === 'DELIVERED' || status === '4' || status === '5';
  if (awaiting) {
    return shipping === 'SELLER'
      ? { ...none, uploadWaybill: true }
      : { ...none, ship: true };
  }
  if (collecting) {
    return shipping === 'SELLER'
      ? { ...none, track: true }
      : { ...none, printLabel: true, track: true };
  }
  if (moving) return { ...none, track: true };
  return none;
}

const FULFILLMENT_RANK = {
  UNPAID: 10,
  ON_HOLD: 20,
  AWAITING_SHIPMENT: 30,
  1: 30,
  2: 30,
  PARTIALLY_SHIPPING: 40,
  AWAITING_COLLECTION: 50,
  3: 50,
  7: 50,
  IN_TRANSIT: 60,
  4: 60,
  DELIVERED: 70,
  5: 70,
  COMPLETED: 80,
  CANCELLED: 90,
  CANCELED: 90,
};

/** 平台详情还停在旧状态时，把本地展示推进到发货后的状态，不回退已完成或已取消。 */
export function advanceOrderStatus(row, nextStatus) {
  if (!row || !nextStatus) return row;
  const current = String(row.orderStatus || row.status || '').trim().toUpperCase();
  const next = String(nextStatus).trim().toUpperCase();
  const currentRank = FULFILLMENT_RANK[current] || 0;
  const nextRank = FULFILLMENT_RANK[next] || 0;
  if (currentRank >= 80 || nextRank <= currentRank) return row;
  const payload = row.payload && typeof row.payload === 'object'
    ? { ...row.payload, status: next }
    : row.payload;
  return { ...row, status: next, orderStatus: next, payload };
}

export function detailReceiveRows(row) {
  const order = tiktokOrder(row);
  const addr = order.recipient_address && typeof order.recipient_address === 'object'
    ? order.recipient_address
    : {};
  const msg = order.receiveMsg && typeof order.receiveMsg === 'object' ? order.receiveMsg : {};
  const lines = [addr.address_line1, addr.address_line2, addr.address_line3, addr.address_line4]
    .filter(present)
    .join(' ');
  const dropOff = addr.delivery_preferences?.drop_off_location;
  return [
    rowIf('收件人', addr.name || [addr.first_name, addr.last_name].filter(present).join(' ')),
    rowIf('电话', addr.phone_number),
    rowIf('国家', addr.region_code || pickField(msg, 'country')),
    rowIf('省/州', districtName(addr, 'L1') || pickField(msg, 'province')),
    rowIf('城市', districtName(addr, 'L2') || addr.post_town || pickField(msg, 'city')),
    rowIf('区/县', districtName(addr, 'L3')),
    rowIf('邮编', addr.postal_code || pickField(msg, 'postCode')),
    rowIf('详细地址', lines || addr.address_detail || addr.full_address),
    rowIf('投放位置', dropOff),
  ].filter(Boolean);
}

export function trackInfoList(payload) {
  if (!payload) return [];
  if (Array.isArray(payload.trackInfo)) return payload.trackInfo;
  if (Array.isArray(payload?.info?.trackInfo)) return payload.info.trackInfo;
  if (Array.isArray(payload?.track?.trackInfo)) return payload.track.trackInfo;
  if (Array.isArray(payload)) return payload;
  return [];
}

export function formatTrackTime(ms) {
  const n = Number(ms);
  if (!Number.isFinite(n) || n <= 0) return '—';
  try {
    return new Date(n).toLocaleString(i18n.language || 'zh-CN', { hour12: false });
  } catch {
    return String(ms);
  }
}

export const DETAIL_KNOWN_TOP_KEYS = new Set([
  'orderNo', 'orderNumber', 'orderStatus', 'orderType', 'salesSite', 'site', 'stockMode',
  'printOrderStatus', 'orderLogisticsType', 'orderPlaceType', 'optionalLogisticsList',
  'billNo', 'isCod', 'isOverLimitOrder', 'salesArea', 'storeCode',
  'orderTime', 'paymentTime', 'orderAllocateTime', 'orderReceiptTime', 'orderMsgUpdateTime',
  'printingTime', 'scheduleDeliveryTime', 'sellerDeliveryTime', 'pickUpTime',
  'requestPrintTime', 'needDeliveryTime', 'requestHandoverTime', 'requestSignTime',
  'orderRejectionTime', 'orderReturnTime', 'orderReportedLossTime', 'warehouseDeliveryTime',
  'expectedCollectTime', 'orderCurrency', 'saleCurrency', 'currency', 'orderAmount',
  'estimatedGrossIncome', 'productTotalPrice', 'totalCostPrice', 'settleActuallyPrice',
  'sellerShippingFee', 'storeDiscountTotalPrice', 'promotionDiscountTotalPrice',
  'totalSettleCurrencyPromotionPrice', 'totalCommission', 'totalPerformanceServiceCharge',
  'totalServiceCharge', 'totalSaleTax', 'totalSellerRealTax', 'totalMxTaxPrice',
  'receiveMsg', 'orderGoodsInfoList', 'packageWaybillList', 'paymentInfo',
  'ipeLogisticsTracks', 'unProcessReason', 'orderTag', 'orderLabels', 'performanceType',
  'performanceBillingType', 'deliveryServiceType', 'lastSyncedAt', 'id',
]);
